/**
 * Measurement sets: named groups of a scene's saved (default) measurements, e.g. "Main facade",
 * "Left wall", "Roof", so they can be shown or hidden together instead of dozens overlapping.
 *
 * Scene data: `measurementSets: [{ id, name, order }]`, and a `setId` on each saved distance or
 * area measurement. A missing or unknown `setId` means "no set": the API calls those
 * "Other measurements" (id 'other').
 *
 * The creator organises measurements into sets in the editor. Scene scripts / host pages show or
 * hide sets through the viewer API (getMeasurementSets, setMeasurementSetVisible,
 * setAllMeasurementSetsVisible, the measurementSetsChange event), and visitors do in the Measure
 * panel when the creator offers them there (dynamic-viewer/measurementSetsPicker.ts). Every set
 * starts shown, so a published scene looks exactly as it did before sets existed, unless the
 * creator starts the visitors' sets unticked.
 *
 * This file holds the data side shared by the viewer (createViewer's measurement tool, the API)
 * and the editor (src/utils/measurementSets.ts).
 */
import type { MeasurementSetData, MeasurementSetInfo } from '../types';
import { cleanSurfacePoints, fitSurfacePolygon, type SurfacePoint } from './surfacePolygon';

/** The id the API uses for the saved measurements that belong to no set. */
export const OTHER_MEASUREMENTS_SET_ID = 'other';
/** What they are called (getMeasurementSets). */
export const OTHER_MEASUREMENTS_LABEL = 'Other measurements';
/** Longest set name kept (longer names are cut). */
export const MAX_MEASUREMENT_SET_NAME = 80;

/**
 * The scene's measurement sets in menu order: entries without a usable id are dropped, repeated
 * ids keep the first, the reserved id 'other' is dropped (its measurements become "no set"), and
 * an empty name becomes "Set n". Unknown fields are kept (sets may grow fields later).
 */
export function normalizeMeasurementSets(raw: unknown): MeasurementSetData[] {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const kept: Array<{ set: MeasurementSetData; order: number; index: number }> = [];
    raw.forEach((entry, index) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
        const source = entry as Record<string, unknown>;
        const id = typeof source.id === 'string' ? source.id.trim() : '';
        if (!id || id === OTHER_MEASUREMENTS_SET_ID || seen.has(id)) return;
        seen.add(id);
        const rawName = typeof source.name === 'string' ? source.name.trim() : '';
        const order = typeof source.order === 'number' && Number.isFinite(source.order) ? source.order : index;
        kept.push({
            set: { ...source, id, name: rawName ? rawName.slice(0, MAX_MEASUREMENT_SET_NAME) : '', order },
            order,
            index,
        });
    });
    kept.sort((a, b) => a.order - b.order || a.index - b.index);
    return kept.map(({ set }, i) => (set.name ? set : { ...set, name: `Set ${i + 1}` }));
}

/** Anything that may sit in a set: a saved distance or area measurement. */
export interface MeasurementSetMember {
    setId?: unknown;
}

/**
 * Which sets a viewer shows. Built from the scene's sets and its saved measurements (for the
 * counts and "Other measurements"); every set starts shown. Visitors' own measurements are never
 * in a set and are not affected.
 */
export class MeasurementSetsState {
    private readonly sets: MeasurementSetData[];
    private readonly ids: Set<string>;
    private readonly counts = new Map<string, number>();
    private readonly hidden = new Set<string>();
    private readonly listeners = new Set<() => void>();

    constructor(sets: MeasurementSetData[], members: readonly MeasurementSetMember[]) {
        this.sets = sets;
        this.ids = new Set(sets.map((s) => s.id));
        for (const member of members) {
            const key = this.keyOf(member?.setId);
            this.counts.set(key, (this.counts.get(key) ?? 0) + 1);
        }
    }

    /** The set a saved measurement is in: its set's id, or 'other' for none / an unknown one. */
    keyOf(setId: unknown): string {
        return typeof setId === 'string' && this.ids.has(setId) ? setId : OTHER_MEASUREMENTS_SET_ID;
    }

    /** True when a set holds at least one saved measurement (something to pick between). */
    get active(): boolean {
        return this.sets.some((s) => (this.counts.get(s.id) ?? 0) > 0);
    }

    /** Whether a saved measurement in this set shows. */
    isShown(setId: unknown): boolean {
        return !this.hidden.has(this.keyOf(setId));
    }

    /** Every set in menu order, then "Other measurements" when some saved measurements are in no set. */
    list(): MeasurementSetInfo[] {
        const out: MeasurementSetInfo[] = this.sets.map((s) => ({
            id: s.id,
            name: s.name,
            visible: !this.hidden.has(s.id),
            count: this.counts.get(s.id) ?? 0,
        }));
        const other = this.counts.get(OTHER_MEASUREMENTS_SET_ID) ?? 0;
        if (other > 0) {
            out.push({ id: OTHER_MEASUREMENTS_SET_ID, name: OTHER_MEASUREMENTS_LABEL, visible: !this.hidden.has(OTHER_MEASUREMENTS_SET_ID), count: other });
        }
        return out;
    }

    /** What a visitor picker would list: the sets that hold measurements, then "Other measurements". */
    menuEntries(): MeasurementSetInfo[] {
        return this.active ? this.list().filter((e) => e.count > 0) : [];
    }

    /** Ids of the shown entries of list(), in order. */
    visibleIds(): string[] {
        return this.list().filter((e) => e.visible).map((e) => e.id);
    }

    /** True when every entry of menuEntries() is shown. */
    allVisible(): boolean {
        return this.menuEntries().every((e) => e.visible);
    }

    /** True when no entry of menuEntries() is shown. */
    noneVisible(): boolean {
        return this.menuEntries().every((e) => !e.visible);
    }

    private known(id: unknown): id is string {
        return typeof id === 'string' && (this.ids.has(id) || id === OTHER_MEASUREMENTS_SET_ID);
    }

    /** Show or hide one set ('other' = the measurements in no set). False for an unknown id. */
    setVisible(id: unknown, visible: boolean): boolean {
        if (!this.known(id)) return false;
        const changed = visible ? this.hidden.delete(id) : !this.hidden.has(id) && !!this.hidden.add(id);
        if (changed) this.notify();
        return true;
    }

    /** Show or hide every set (and the measurements in no set). */
    setAllVisible(visible: boolean): void {
        const before = this.hidden.size;
        if (visible) this.hidden.clear();
        else {
            for (const s of this.sets) this.hidden.add(s.id);
            this.hidden.add(OTHER_MEASUREMENTS_SET_ID);
        }
        if (this.hidden.size !== before) this.notify();
    }

    /** Called after every change of what is shown; returns the unsubscribe. */
    onChange(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify(): void {
        for (const listener of [...this.listeners]) listener();
    }
}

/**
 * Whether the viewer draws one measurement now. Saved (locked) measurements show when their set
 * is shown (in or out of measurement mode; the caller decides whether anything renders outside
 * it). A visitor's own measurements show while measuring, as before sets existed.
 */
export function measurementShown(
    item: { locked?: boolean; setId?: unknown },
    measureMode: boolean,
    sets: MeasurementSetsState | null,
): boolean {
    if (!item.locked) return measureMode;
    return !sets || sets.isShown(item.setId);
}

/**
 * Whether a scene ships its saved measurements to visitors: `includeDefaultMeasurementsInViewer`,
 * or for scenes saved before that flag existed, `defaultMeasurementsVisibleOnLoad`.
 */
export function includesDefaultMeasurements(uiOptions: { includeDefaultMeasurementsInViewer?: boolean; defaultMeasurementsVisibleOnLoad?: boolean } | undefined): boolean {
    return !!(uiOptions?.includeDefaultMeasurementsInViewer ?? uiOptions?.defaultMeasurementsVisibleOnLoad ?? false);
}

/** The scene fields measurement sets are read from. */
export interface SceneMeasurementSetSource {
    measurementSets?: unknown;
    measurements?: Array<{ pointA?: unknown; pointB?: unknown; setId?: unknown } | null | undefined>;
    areaMeasurements?: Array<{ points?: unknown; setId?: unknown } | null | undefined>;
    uiOptions?: { measurementsEnabled?: boolean; includeDefaultMeasurementsInViewer?: boolean; defaultMeasurementsVisibleOnLoad?: boolean };
}

/**
 * A viewer's measurement sets: null when the scene has no measurement tool, ships no saved
 * measurements, or has no sets. Counts are over the saved measurements the viewer draws (the
 * ones the measurement tool accepts: both points of a distance, an area with three usable points).
 */
export function sceneMeasurementSets(scene: SceneMeasurementSetSource): MeasurementSetsState | null {
    if (!scene.uiOptions?.measurementsEnabled || !includesDefaultMeasurements(scene.uiOptions)) return null;
    const sets = normalizeMeasurementSets(scene.measurementSets);
    if (sets.length === 0) return null;
    const members: MeasurementSetMember[] = [];
    if (Array.isArray(scene.measurements)) {
        for (const m of scene.measurements) if (m && m.pointA && m.pointB) members.push({ setId: m.setId });
    }
    if (Array.isArray(scene.areaMeasurements)) {
        for (const a of scene.areaMeasurements) {
            const points = cleanSurfacePoints(a?.points as SurfacePoint[] | undefined);
            if (points.length >= 3 && fitSurfacePolygon(points)) members.push({ setId: a?.setId });
        }
    }
    return new MeasurementSetsState(sets, members);
}

/** The scene-script API over a viewer's sets (null while the scene has none). */
export interface MeasurementSetsApi {
    getMeasurementSets(): MeasurementSetInfo[];
    setMeasurementSetVisible(id: string, visible: boolean): boolean;
    setAllMeasurementSetsVisible(visible: boolean): void;
}

export function createMeasurementSetsApi(getState: () => MeasurementSetsState | null): MeasurementSetsApi {
    return {
        getMeasurementSets: () => getState()?.list() ?? [],
        setMeasurementSetVisible: (id, visible) => getState()?.setVisible(id, visible === true) ?? false,
        // Like setAllLayerGroupsVisible: anything but false shows them (no argument = show all).
        setAllMeasurementSetsVisible: (visible) => getState()?.setAllVisible(visible !== false),
    };
}
